import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';

export interface StepDefinition {
  label: string;
  id: string;
}

interface StepProgressBarProps {
  steps: StepDefinition[];
  currentStepIndex: number;
  /** When true, the last real step is shown as completed (all steps filled). */
  isLastStepCompleted?: boolean;
  /** Marks a step as failed — shows a red error icon and label. */
  errorStepIndex?: number;
  /**
   * Optional percent positions for steps after the first (excludes sentinel).
   * Use when default even spacing causes label overlap on long step names.
   */
  stepPositionPercents?: number[];
}

// SVG circle icon with dashed stroke — used for pending and current steps
function DashedCircleIcon({ isCurrent }: { isCurrent: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {/* Left half fill — only shown for the current step */}
      {isCurrent && <path d="M12 2 A10 10 0 0 0 12 22 Z" fill="#0f62fe" />}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke={isCurrent ? '#0f62fe' : '#161616'}
        strokeWidth="2"
        strokeDasharray="4 3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// Solid filled circle with checkmark — used for completed steps
function CompletedCircleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" fill="#0f62fe" />
      <path
        d="M7 12.5l3.5 3.5 6.5-7"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ErrorCircleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" fill="#da1e28" />
      <path
        d="M12 7v6M12 16.5v.5"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const StepProgressBar: React.FC<StepProgressBarProps> = ({
  steps,
  currentStepIndex,
  isLastStepCompleted = false,
  errorStepIndex,
  stepPositionPercents
}) => {
  // Visually advance one extra index so the last real step shows as completed.
  // The sentinel dot absorbs this bump — it becomes the "current" marker while
  // every real step dot turns blue/checked.
  const visualStepIndex = isLastStepCompleted
    ? currentStepIndex + 1
    : currentStepIndex;
  const trackRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [fillWidth, setFillWidth] = useState<string>('0px');

  /*
   * We append a hidden sentinel dot after the real last step so that the final
   * labelled step is centred (like all middle steps) rather than right-anchored.
   * This gives the last label the same left-padding breathing room as the others:
   *
   *   x----------x----------x----------x----------x
   *   First        Step 2       Step 3       Last   (hidden)
   *
   * The sentinel is purely cosmetic — it has no id/label and is never "active".
   */
  const renderSteps = [...steps, { id: '__sentinel__', label: '' }];
  const lastIndex = renderSteps.length - 1;

  /**
   * Measures each step element's left offset relative to the track container,
   * then sets the fill width to reach just before the NEXT step's icon.
   *
   * WHY: percentage-based fill can't align precisely with step icon positions
   * because the first step is left-edge-anchored (not centred), so its rendered
   * pixel position doesn't match a simple percentage formula.
   */
  const computeFillWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const trackRect = track.getBoundingClientRect();

    // Fill to the sentinel when all real steps are complete
    if (visualStepIndex >= steps.length - 1) {
      setFillWidth('100%');
      return;
    }

    const nextStepEl = stepRefs.current[visualStepIndex + 1];
    if (!nextStepEl) return;

    // Left edge of the next step's icon relative to the track start
    const nextStepRect = nextStepEl.getBoundingClientRect();
    const fillPx = nextStepRect.left - trackRect.left;
    setFillWidth(`${fillPx}px`);
  }, [visualStepIndex, steps.length]);

  // Recompute on mount, step change, and window resize
  useLayoutEffect(() => {
    computeFillWidth();

    const handleResize = () => computeFillWidth();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [computeFillWidth]);

  return (
    <div className="step-progress-bar">
      {/* Single timeline across the full width */}
      <div ref={trackRef} className="step-progress-bar__track">
        <div
          className="step-progress-bar__track-fill"
          style={{ width: fillWidth }}
        />
      </div>

      {/*
       * All steps use translateX(-50%) to centre over their track position,
       * except the first which is left-anchored. The hidden sentinel at the end
       * is right-anchored so the track fills edge-to-edge.
       */}
      <div className="step-progress-bar__steps">
        {renderSteps.map((step, index) => {
          const isSentinel = step.id === '__sentinel__';
          const isCompleted = index < visualStepIndex;
          const isCurrent = index === visualStepIndex;
          const isError = errorStepIndex != null && index === errorStepIndex;

          const defaultPercent = (index / lastIndex) * 100;
          const customPercent = stepPositionPercents?.[index - 1];
          const posPercent = customPercent ?? defaultPercent;

          const positionStyle: React.CSSProperties =
            index === 0
              ? { left: 0 }
              : isSentinel
              ? { right: 0 }
              : { left: `${posPercent}%`, transform: 'translateX(-50%)' };

          return (
            <div
              key={step.id}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              aria-hidden={isSentinel || undefined}
              className={[
                'step-progress-bar__step',
                isCompleted ? 'step-progress-bar__step--completed' : '',
                isCurrent ? 'step-progress-bar__step--current' : '',
                isError ? 'step-progress-bar__step--error' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              style={positionStyle}
            >
              <div className="step-progress-bar__icon">
                {isCompleted ? (
                  <CompletedCircleIcon />
                ) : isError ? (
                  <ErrorCircleIcon />
                ) : lastIndex === index ? null : (
                  <DashedCircleIcon isCurrent={isCurrent} />
                )}
              </div>
              {!isSentinel && (
                <span className="step-progress-bar__label">{step.label}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepProgressBar;
